import { useRouter } from 'expo-router';
import { BellOff, CheckCheck, X } from 'lucide-react-native';
import { useMemo } from 'react';
import { ActivityIndicator, Pressable, SectionList, StyleSheet, View } from 'react-native';
import { HStack, Sheet, Text, VStack } from '../../../components/primitives';
import { useAuth } from '../../../contexts/AuthContext';
import { useTranslation } from '../../../contexts/TranslationContext';
import type { NotificationItem as NotificationItemType } from '../../../lib/api';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import { useMarkAllAsRead, useMarkAsRead, useNotificationList } from '../hooks/useNotifications';
import { NotificationItem } from './NotificationItem';

interface NotificationSection {
  key: 'today' | 'yesterday' | 'earlier';
  title: string;
  data: NotificationItemType[];
}

function groupByTime(
  notifications: NotificationItemType[],
  labels: { today: string; yesterday: string; earlier: string },
): NotificationSection[] {
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const startOfYesterday = new Date(startOfToday);
  startOfYesterday.setDate(startOfToday.getDate() - 1);

  const today: NotificationItemType[] = [];
  const yesterday: NotificationItemType[] = [];
  const earlier: NotificationItemType[] = [];

  for (const n of notifications) {
    const t = new Date(n.createdAt).getTime();
    if (t >= startOfToday.getTime()) today.push(n);
    else if (t >= startOfYesterday.getTime()) yesterday.push(n);
    else earlier.push(n);
  }

  return (
    [
      { key: 'today' as const, title: labels.today, data: today },
      { key: 'yesterday' as const, title: labels.yesterday, data: yesterday },
      { key: 'earlier' as const, title: labels.earlier, data: earlier },
    ] satisfies NotificationSection[]
  ).filter((s) => s.data.length > 0);
}

interface Props {
  visible: boolean;
  onClose: () => void;
}

interface ContentProps {
  onClose: () => void;
  /**
   * When true, the component renders its own title bar (used when the body
   * is embedded directly into a drawer instead of a Sheet). The Sheet
   * primitive already provides its own header, so the parent suppresses it.
   */
  withHeader?: boolean;
}

export function NotificationFeedContent({ onClose, withHeader = false }: Readonly<ContentProps>) {
  const { currentOrganization } = useAuth();
  const { t } = useTranslation();
  const router = useRouter();
  const orgId = currentOrganization?.id;

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useNotificationList(orgId);

  const markAsRead = useMarkAsRead();
  const markAllAsRead = useMarkAllAsRead();

  const notifications = data?.pages.flatMap((p) => p.notifications) ?? [];

  const sections = useMemo(
    () =>
      groupByTime(notifications, {
        today: t('notifications.sectionToday'),
        yesterday: t('notifications.sectionYesterday'),
        earlier: t('notifications.sectionEarlier'),
      }),
    [notifications, t],
  );

  const handleNotificationPress = (notification: NotificationItemType) => {
    if (!notification.readAt) {
      markAsRead.mutate(notification.id);
    }

    const leadId = notification.data?.leadId as string | undefined;
    if (leadId) {
      onClose();
      router.push(`/lead/${leadId}` as never);
    }
  };

  const handleMarkAllAsRead = () => {
    if (orgId) {
      markAllAsRead.mutate(orgId);
    }
  };

  return (
    <View style={[styles.container, withHeader && styles.containerWithHeader]}>
      {withHeader && (
        <View style={styles.header}>
          <Text variant="h6" style={styles.headerTitle} numberOfLines={1}>
            {t('notifications.title')}
          </Text>
          <Pressable
            onPress={onClose}
            hitSlop={8}
            accessibilityRole="button"
            accessibilityLabel="Close"
            style={styles.closeButton}
          >
            <X size={20} color={colors.text.tertiary} />
          </Pressable>
        </View>
      )}

      {notifications.length > 0 && (
        <Pressable
          onPress={handleMarkAllAsRead}
          style={styles.markAllButton}
          accessibilityRole="button"
          accessibilityLabel={t('notifications.markAllRead')}
        >
          <HStack spacing="xs" align="center">
            <CheckCheck size={14} color={colors.primary[500]} strokeWidth={1.8} />
            <Text variant="caption" color={colors.primary[500]}>
              {t('notifications.markAllRead')}
            </Text>
          </HStack>
        </Pressable>
      )}

      {isLoading && (
        <View style={styles.centered}>
          <ActivityIndicator color={colors.primary[500]} />
        </View>
      )}
      {!isLoading && notifications.length === 0 && (
        <VStack spacing="md" align="center" style={styles.emptyState}>
          <BellOff size={40} color={colors.text.disabled} strokeWidth={1.2} />
          <Text variant="body" color={colors.text.tertiary} style={styles.emptyText}>
            {t('notifications.empty')}
          </Text>
        </VStack>
      )}
      {!isLoading && notifications.length > 0 && (
        <SectionList
          sections={sections}
          keyExtractor={(item) => item.id}
          renderItem={({ item }) => (
            <NotificationItem notification={item} onPress={handleNotificationPress} />
          )}
          renderSectionHeader={({ section }) => (
            <View style={styles.sectionHeader}>
              <Text variant="caption" color={colors.text.tertiary} style={styles.sectionHeaderText}>
                {section.title.toUpperCase()}
              </Text>
            </View>
          )}
          stickySectionHeadersEnabled={false}
          onEndReached={() => {
            if (hasNextPage && !isFetchingNextPage) {
              fetchNextPage();
            }
          }}
          onEndReachedThreshold={0.5}
          ListFooterComponent={
            isFetchingNextPage ? (
              <View style={styles.footerLoader}>
                <ActivityIndicator size="small" color={colors.primary[500]} />
              </View>
            ) : null
          }
          showsVerticalScrollIndicator={false}
        />
      )}
    </View>
  );
}

export function NotificationFeedSheet({ visible, onClose }: Readonly<Props>) {
  const { t } = useTranslation();

  return (
    <Sheet
      visible={visible}
      onClose={onClose}
      title={t('notifications.title')}
      size="sm"
      position="right"
    >
      <NotificationFeedContent onClose={onClose} />
    </Sheet>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    marginHorizontal: -tokens.spacing.xl,
    marginBottom: -tokens.spacing.xl,
  },
  containerWithHeader: {
    marginHorizontal: 0,
    marginBottom: 0,
    backgroundColor: colors.background.secondary,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: tokens.spacing.xl,
    paddingVertical: tokens.spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: colors.border.muted,
  },
  headerTitle: {
    flex: 1,
    marginRight: tokens.spacing.md,
  },
  closeButton: {
    padding: tokens.spacing.xs,
    borderRadius: tokens.radius.sm,
  },
  markAllButton: {
    paddingHorizontal: tokens.spacing.lg,
    paddingVertical: tokens.spacing.sm,
    alignSelf: 'flex-end',
  },
  centered: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: tokens.spacing['4xl'],
  },
  emptyState: {
    paddingVertical: tokens.spacing['4xl'],
  },
  emptyText: {
    textAlign: 'center',
  },
  footerLoader: {
    paddingVertical: tokens.spacing.lg,
    alignItems: 'center',
  },
  sectionHeader: {
    paddingHorizontal: tokens.spacing.md,
    paddingTop: tokens.spacing.md,
    paddingBottom: tokens.spacing.xs,
    backgroundColor: colors.background.secondary,
  },
  sectionHeaderText: {
    letterSpacing: 1.2,
    fontWeight: '600',
  },
});
